/**
 * Pure helpers over the backend status catalog (GET /v1/api/orders/status-catalog).
 * This module and the fallback JSON are the ONLY places in the customer app allowed to name
 * status keys as data; screens go through these functions (epic §13).
 */
import fallback from './statusCatalog.fallback.json'

export type ColorToken = 'neutral' | 'warning' | 'info' | 'primary' | 'success' | 'danger' | 'muted'
export type ActorType = 'customer' | 'vendor' | 'admin' | 'system' | 'integration'
export type Locale = 'en' | 'ar'
export type StatusModel = 'legacy' | 'v2'
export interface StatusValue { key: string; label_en: string; label_ar: string; color_token: ColorToken; rank: number; terminal: boolean; customer_visible: boolean }
export interface Edge { from: string; to: string }
/** `kinds` (order_item only, catalog 2026-10-04.1): transitions and labels per item kind; the customer app shows no item chips and does not read it. */
export interface CatalogEntity { values: StatusValue[]; transitions: Partial<Record<ActorType, Record<string, string[]>>>; reason_required?: string[]; reason_required_edges?: Edge[]; reason_codes?: { rejected: string[] }; unreleased?: string[]; inactive?: string[]; kinds?: Record<string, { transitions: Partial<Record<ActorType, Record<string, string[]>>>; labels?: Record<string, { label_en: string; label_ar: string }> }> }
export interface LegacyItemEntity { values: StatusValue[]; transitions: Partial<Record<ActorType, { product: Record<string, string[]>; venue: Record<string, string[]> }>> }
export interface StatusCatalog {
  version: string; actor_types: ActorType[]; sources: string[]
  legacy: { order: CatalogEntity & { customer_cancel_reason_codes: string[] }; order_item: LegacyItemEntity }
  v2: { fulfillment: CatalogEntity; payment: CatalogEntity; quote: CatalogEntity; order_item: CatalogEntity; vendor_group: CatalogEntity; display: CatalogEntity }
}
export type V2Entity = keyof StatusCatalog['v2']
export type Entity = V2Entity | 'legacy_order' | 'legacy_item'

export const FALLBACK_CATALOG = (fallback as { data: unknown }).data as StatusCatalog

/** Catalog colour token → literal Tailwind classes (brand purple for primary). */
export const TOKEN_CLASSES: Record<ColorToken, string> = {
  neutral: 'bg-gray-100 text-gray-800',
  muted: 'bg-gray-100 text-gray-500',
  info: 'bg-blue-100 text-blue-800',
  primary: 'bg-purple-100 text-purple-800',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-orange-100 text-orange-800',
  danger: 'bg-red-100 text-red-800',
}

/** Well-known keys the screens branch on (mirrors mila_me_backend/src/constants/orderStatus.js and orderRollup.deriveDisplayStatus). */
export const KEYS = Object.freeze({
  display: {
    draft: 'draft', pending: 'pending', pending_payment: 'pending_payment', partially_paid: 'partially_paid', under_review: 'under_review', generated: 'generated',
    confirmed: 'confirmed', accepted: 'accepted', in_progress: 'in_progress', ready_for_delivery: 'ready_for_delivery', partially_delivered: 'partially_delivered',
    delivered: 'delivered', canceled: 'canceled', rejected: 'rejected', expired: 'expired',
  },
  payment: { pending: 'pending', paid: 'paid', partially_paid: 'partially_paid', refunded: 'refunded', partially_refunded: 'partially_refunded', voided: 'voided' },
  paymentMethod: { cod: 'cod' },
  reason: { other: 'other' },
})

/** Buy-now-pay-later methods: never used for a deposit or a balance (mirrors paymentController BNPL_METHODS). */
export const BNPL_METHODS: readonly string[] = Object.freeze(['tabby', 'tamara', 'tabby-hyperpay'])

/**
 * Legacy orders.status → display key, for responses that carry no display_status (older backend, non-presented
 * endpoints). Mirrors orderRollup.legacyToFulfillment + deriveDisplayStatus for the customer audience (decision 7:
 * rejected reads as canceled; refunded reads as canceled with the refund shown by the payment chip).
 */
export const LEGACY_TO_DISPLAY: Readonly<Record<string, string>> = Object.freeze({
  created: 'pending', pending_payment: 'pending_payment', timeout: 'expired', paid: 'confirmed', in_progress: 'in_progress',
  ready_for_delivery: 'ready_for_delivery', delivered: 'delivered', canceled: 'canceled', refunded: 'canceled',
  under_review: 'under_review', generated: 'generated', accepted: 'accepted', rejected: 'canceled',
})
export const legacyToDisplay = (status: string): string => LEGACY_TO_DISPLAY[status] ?? status
export const displayOf = (order: { status: string; display_status?: string | null }): string => order.display_status || legacyToDisplay(order.status)

/** The order's status model; a missing `status_model` (older payloads) means 'legacy'. */
export const statusModelOf = (order: { status_model?: StatusModel | null }): StatusModel => (order.status_model === 'v2' ? 'v2' : 'legacy')

const PAYABLE_DISPLAY = [KEYS.display.draft, KEYS.display.pending, KEYS.display.pending_payment, KEYS.display.partially_paid]
/** The customer may still pay: unpaid and not yet released / closed. */
export const isPayableOrder = (order: { status: string; display_status?: string | null; is_paid?: boolean }): boolean =>
  order.is_paid !== true && PAYABLE_DISPLAY.includes(displayOf(order))

/**
 * Story 4.1: the customer may pay what is still owed on the order (a deposit order's balance). The backend decides
 * (can_pay_balance: mode on, non-COD, non-terminal, balance due, released or deposit paid); a released deposit order
 * is not isPayableOrder, so the two predicates split the UI (Pay now vs Pay balance).
 */
export const canPayBalance = (order: { can_pay_balance?: boolean; is_paid?: boolean }): boolean =>
  order.can_pay_balance === true && order.is_paid !== true

/** A deposit-plan row status (pending | paid | canceled) → the catalog payment key its chip shows. */
export const scheduleRowPaymentKey = (status: string): string =>
  status === KEYS.payment.paid ? KEYS.payment.paid : status === 'canceled' ? KEYS.payment.voided : KEYS.payment.pending

const valuesOfEntity = (catalog: StatusCatalog, entity: Entity): StatusValue[] =>
  entity === 'legacy_order' ? catalog.legacy.order.values : entity === 'legacy_item' ? catalog.legacy.order_item.values : catalog.v2[entity].values

export const humanize = (key: string): string => key.replace(/_/g, ' ').replace(/^\w/, (ch) => ch.toUpperCase())
export const findValue = (catalog: StatusCatalog, entity: Entity, key: string | null | undefined): StatusValue | undefined =>
  key == null || key === '' ? undefined : valuesOfEntity(catalog, entity).find((v) => v.key === key)
export const labelFor = (catalog: StatusCatalog, entity: Entity, key: string | null | undefined, locale: Locale): string => {
  if (key == null || key === '') return '—'
  const v = findValue(catalog, entity, key)
  if (!v) return humanize(key)
  return locale === 'ar' ? v.label_ar : v.label_en
}
export const colorFor = (catalog: StatusCatalog, entity: Entity, key: string | null | undefined): ColorToken => findValue(catalog, entity, key)?.color_token ?? 'neutral'
export const rankOf = (catalog: StatusCatalog, entity: Entity, key: string | null | undefined): number => findValue(catalog, entity, key)?.rank ?? -1
export const isTerminal = (catalog: StatusCatalog, entity: Entity, key: string | null | undefined): boolean => findValue(catalog, entity, key)?.terminal === true
export const valuesOf = (catalog: StatusCatalog, entity: Entity): StatusValue[] => [...valuesOfEntity(catalog, entity)].sort((a, b) => a.rank - b.rank)
export const customerCancelReasonCodes = (catalog: StatusCatalog): string[] => catalog.legacy.order.customer_cancel_reason_codes ?? []
